import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDropzone } from "react-dropzone";
import { toPng } from "html-to-image";
import {
    UploadCloud,
    FileText,
    Search,
    RotateCcw,
    Download,
    Building2,
    Table2,
    Info,
    Layers3,
} from "lucide-react";

const errMsg = (e) => e?.response?.data?.error || e?.message || "Request failed";

function friendlyNetworkError(e) {
    const status = e?.response?.status;
    if (!status && e?.message === "Network Error") {
        return "Network Error. Please try again later.";
    }
    return errMsg(e);
}

function cx(...xs) {
    return xs.filter(Boolean).join(" ");
}

function fmt(v, digits = 2) {
    return typeof v === "number" && Number.isFinite(v) ? v.toFixed(digits) : "—";
}

function titleizeSlug(v) {
    return String(v || "")
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function normaliseManufacturers(payload) {
    const raw = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.manufacturers)
            ? payload.manufacturers
            : Array.isArray(payload?.companies)
                ? payload.companies
                : Array.isArray(payload?.data)
                    ? payload.data
                    : [];

    return [...new Set(
        raw
            .map((item) => {
                if (typeof item === "string") return item;
                return item?.manufacturer || item?.company || item?.name || item?.label || "";
            })
            .filter(Boolean)
    )];
}

function Badge({ tone = "slate", children }) {
    const tones = {
        slate: "bg-slate-100 text-slate-700 border-slate-200",
        emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
        amber: "bg-amber-50 text-amber-800 border-amber-200",
        sky: "bg-sky-50 text-sky-700 border-sky-200",
        rose: "bg-rose-50 text-rose-700 border-rose-200",
    };

    return (
        <span
            className={cx(
                "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold",
                tones[tone]
            )}
        >
            {children}
        </span>
    );
}

function SelectField({ value, onChange, children, disabled }) {
    return (
        <select
            value={value}
            onChange={onChange}
            disabled={disabled}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-400/70 disabled:bg-slate-50 disabled:opacity-60"
        >
            {children}
        </select>
    );
}

function NumberField({ value, onChange, disabled, step = 0.001 }) {
    return (
        <input
            type="number"
            step={step}
            value={value ?? ""}
            onChange={onChange}
            disabled={disabled}
            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 shadow-sm outline-none focus:ring-2 focus:ring-slate-400/70 disabled:bg-slate-50 disabled:opacity-60"
        />
    );
}

function sanitiseRowsForResolve(rows) {
    return rows.map((row) => ({
        row_key: row.row_key,
        raw_pdf_code: row.raw_pdf_code || "",
        source_window_id: row.source_window_id || "",
        source_window_description: row.source_window_description || "",
        source_product_type: row.source_product_type || "",
        source_u: row.source_u == null || row.source_u === "" ? null : Number(row.source_u),
        source_s: row.source_s == null || row.source_s === "" ? null : Number(row.source_s),
        product_type: row.product_type || "",
        u_mode: row.u_mode || "nearest",
        u_target: row.u_target === "" || row.u_target == null ? null : Number(row.u_target),
        s_mode: row.s_mode || "nearest",
        s_target: row.s_target === "" || row.s_target == null ? null : Number(row.s_target),
    }));
}

async function fetchManufacturers(listBasePath) {
    try {
        const liteResponse = await axiosInstance.get(`${listBasePath}/manufacturers-lite`);
        const liteList = normaliseManufacturers(liteResponse.data);
        if (liteList.length > 0) {
            return liteList;
        }
    } catch (e) {
        // fall back to the heavier endpoint below
    }

    const { data } = await axiosInstance.get(`${listBasePath}/manufacturers`);
    return normaliseManufacturers(data);
}

async function scanCompanyPdf({ company, file, material = "all", basePath }) {
    const form = new FormData();
    form.append("file", file);
    form.append("material", material);

    const { data } = await axiosInstance.post(
        `${basePath}/${encodeURIComponent(company)}/scan-pdf/async`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
    );

    return data;
}

async function scanCompanyPdfSync({ company, file, material = "all", basePath }) {
    const form = new FormData();
    form.append("file", file);
    form.append("material", material);

    const url = `${basePath}/${encodeURIComponent(company)}/scan-pdf`;
    const transientStatuses = new Set([502, 503, 504]);
    let lastError = null;

    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            const { data } = await axiosInstance.post(url, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        } catch (error) {
            lastError = error;
            const status = error?.response?.status;
            if (!transientStatuses.has(status) || attempt === 1) {
                throw error;
            }
            await new Promise((resolve) => setTimeout(resolve, 1200));
        }
    }

    throw lastError || new Error("PDF scan failed");
}

async function waitForJobResult(jobPath, { pollIntervalMs = 1200, timeoutMs = 20 * 60 * 1000 } = {}) {
    const startedAt = Date.now();
    const transientStatuses = new Set([502, 503, 504]);
    let retryCount = 0;

    while (Date.now() - startedAt < timeoutMs) {
        try {
            const { data } = await axiosInstance.get(jobPath);
            const status = data?.status;

            if (status === "completed") {
                return data?.result;
            }

            if (status === "failed") {
                throw new Error(data?.error || "Background job failed");
            }

            retryCount = 0;
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
        } catch (error) {
            const status = error?.response?.status;
            if (!transientStatuses.has(status) || retryCount >= 5) {
                throw error;
            }

            retryCount += 1;
            const backoffMs = Math.min(pollIntervalMs * (retryCount + 1), 5000);
            await new Promise((resolve) => setTimeout(resolve, backoffMs));
        }
    }

    throw new Error("Background job timed out");
}

function buildCompanyJobPath(jobId, scanBasePath) {
    if (String(scanBasePath || "").includes("/sponsor")) {
        return `/finder/public/jobs/${jobId}`;
    }

    return `/finder/jobs/${jobId}`;
}

async function resolveCompanyRows({ company, rows, material = "all", basePath }) {
    const url = `${basePath}/${encodeURIComponent(company)}/resolve`;
    const transientStatuses = new Set([502, 503, 504]);
    let lastError = null;

    for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
            const { data } = await axiosInstance.post(url, { rows, material });
            return data;
        } catch (error) {
            lastError = error;
            const status = error?.response?.status;
            if (!transientStatuses.has(status) || attempt === 1) {
                throw error;
            }
            await new Promise((resolve) => setTimeout(resolve, 500));
        }
    }

    throw lastError || new Error("Resolve request failed");
}

const DEFAULT_BASE_PATH = "/finder/company";

export default function GlassFinderPerCompany({
    basePath = DEFAULT_BASE_PATH,
    initialCompany = "",
    hideCompanySelector = false,
    showToastContainer = true,
}) {
    const [pdfFile, setPdfFile] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [loadingManufacturers, setLoadingManufacturers] = useState(false);

    const [manufacturers, setManufacturers] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(initialCompany || "");

    const [manufacturer, setManufacturer] = useState("");
    const [codes, setCodes] = useState([]);
    const [unmatchedCodes, setUnmatchedCodes] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [materialFilter, setMaterialFilter] = useState("all");
    const [availableProductTypes, setAvailableProductTypes] = useState([]);
    const [rows, setRows] = useState([]);

    const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
    const [selectedRegisterRow, setSelectedRegisterRow] = useState(null);

    const exportRef = useRef(null);

    const listBasePath = "/finder/company";
    // Local backend auth is bypassed on localhost, so use the normal company
    // route and avoid the sponsor-only manufacturer restriction.
    const scanBasePath = basePath;

    const openRegisterDialog = (row) => {
        setSelectedRegisterRow(row);
        setRegisterDialogOpen(true);
    };

    const closeRegisterDialog = () => {
        setRegisterDialogOpen(false);
        setSelectedRegisterRow(null);
    };

    useEffect(() => {
        if (hideCompanySelector) {
            setSelectedCompany(initialCompany);
            return;
        }

        const loadManufacturers = async () => {
            try {
                setLoadingManufacturers(true);
                const list = await fetchManufacturers(listBasePath);
                setManufacturers(list);

                if (list.length > 0) {
                    setSelectedCompany((prev) => (prev && list.includes(prev) ? prev : list[0]));
                }
            } catch (e) {
                toast.error(`Failed to load companies: ${errMsg(e)}`);
            } finally {
                setLoadingManufacturers(false);
            }
        };

        loadManufacturers();
    }, [basePath, hideCompanySelector, initialCompany]);

    const companyTitle = useMemo(
        () => manufacturer || titleizeSlug(selectedCompany),
        [manufacturer, selectedCompany]
    );

    const handleDrop = React.useCallback((acceptedFiles, fileRejections) => {
        const rejected = fileRejections?.[0];
        if (rejected) {
            toast.error("Please drop a single PDF file");
            return;
        }

        const file = acceptedFiles?.[0];
        if (!file) return;

        const isPdf =
            file.type === "application/pdf" ||
            String(file.name || "").toLowerCase().endsWith(".pdf");

        if (!isPdf) {
            toast.error("Please select a PDF file");
            return;
        }

        setPdfFile(file);
    }, []);

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
        open,
    } = useDropzone({
        onDrop: handleDrop,
        accept: { "application/pdf": [".pdf"] },
        maxFiles: 1,
        multiple: false,
        noClick: true,
        noKeyboard: true,
    });

    const clearResolvedState = () => {
        setManufacturer("");
        setCodes([]);
        setUnmatchedCodes([]);
        setMaterials([]);
        setMaterialFilter("all");
        setAvailableProductTypes([]);
        setRows([]);
    };

    const resetAll = () => {
        setPdfFile(null);
        setProcessing(false);
        setApplying(false);
        setExporting(false);
        clearResolvedState();
    };

    const applyResponse = (data) => {
        setManufacturer(data?.manufacturer || "");
        setCodes(Array.isArray(data?.codes) ? data.codes : []);
        setUnmatchedCodes(Array.isArray(data?.unmatched_codes) ? data.unmatched_codes : []);
        setMaterials(Array.isArray(data?.materials) ? data.materials : []);
        setMaterialFilter(data?.selected_material || "all");
        setAvailableProductTypes(
            Array.isArray(data?.available_product_types) ? data.available_product_types : []
        );
        setRows(Array.isArray(data?.rows) ? data.rows : []);
    };

    const runScan = async () => {
        try {
            if (!selectedCompany) {
                toast.error("No company available");
                return;
            }

            if (!pdfFile) {
                toast.error("Select a PDF first");
                return;
            }

            setProcessing(true);

            toast.info("PDF received. Scanning now...");

            const data = await scanCompanyPdfSync({
                company: selectedCompany,
                file: pdfFile,
                material: materialFilter,
                basePath: scanBasePath,
            });
            applyResponse(data);
            toast.success(`PDF scanned successfully for ${data?.manufacturer || selectedCompany}`);
        } catch (e) {
            toast.error(`PDF scan failed: ${friendlyNetworkError(e)}`);
            clearResolvedState();
        } finally {
            setProcessing(false);
        }
    };

    const updateRow = (rowKey, patch) => {
        setRows((prev) =>
            prev.map((row) => (row.row_key === rowKey ? { ...row, ...patch } : row))
        );
    };

    const handleApply = async (
        nextRows = rows,
        nextMaterial = materialFilter,
        nextCompany = selectedCompany
    ) => {
        try {
            if (!nextCompany) {
                toast.error("No company available");
                return;
            }

            setApplying(true);

            const data = await resolveCompanyRows({
                company: nextCompany,
                rows: sanitiseRowsForResolve(nextRows),
                material: nextMaterial,
                basePath: scanBasePath,
            });

            setManufacturer(data?.manufacturer || "");
            setMaterials(Array.isArray(data?.materials) ? data.materials : materials);
            setMaterialFilter(data?.selected_material || nextMaterial);
            setAvailableProductTypes(
                Array.isArray(data?.available_product_types) ? data.available_product_types : []
            );
            setRows(Array.isArray(data?.rows) ? data.rows : []);
            toast.success("Table updated");
        } catch (e) {
            toast.error(`Failed to update table: ${friendlyNetworkError(e)}`);
        } finally {
            setApplying(false);
        }
    };

    const handleMaterialChange = async (e) => {
        const nextMaterial = e.target.value;
        setMaterialFilter(nextMaterial);

        if (rows.length > 0) {
            await handleApply(rows, nextMaterial, selectedCompany);
        }
    };

    const handleCompanyChange = async (e) => {
        if (hideCompanySelector) return;
        const nextCompany = e.target.value;
        setSelectedCompany(nextCompany);
        setManufacturer("");

        if (rows.length > 0) {
            await handleApply(rows, materialFilter, nextCompany);
        }
    };

    const applyPresetToAll = async (preset) => {
        const updatedRows = rows.map((row) => ({
            ...row,
            u_mode: preset,
            s_mode: preset,
            u_target: row.source_u ?? row.u_target ?? "",
            s_target: row.source_s ?? row.s_target ?? "",
        }));

        setRows(updatedRows);
        await handleApply(updatedRows, materialFilter, selectedCompany);
    };

    const handleExport = async () => {
        if (!exportRef.current) return;

        try {
            setExporting(true);

            const dataUrl = await toPng(exportRef.current, {
                cacheBust: true,
                pixelRatio: 2,
                backgroundColor: "#ffffff",
            });

            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `${(manufacturer || selectedCompany || "company")
                .toLowerCase()
                .replace(/\s+/g, "-")}-window-table.png`;
            a.click();
        } catch (e) {
            toast.error(`Export failed: ${friendlyNetworkError(e)}`);
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Upload */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="px-5 py-5 sm:px-6 sm:py-6">
                    <div className="flex items-start gap-4 text-left">
                        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                            <Building2 className="h-5 w-5" />
                        </div>

                        <div className="min-w-0 w-full">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                                        {companyTitle || "Company"} — Company PDF Selector
                                    </h2>
                                    <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                        Upload a PDF, extract the window codes, then resolve products only from this company.
                                    </p>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge tone="slate">Company scope only</Badge>
                                    <Badge tone="amber">Default scan: max</Badge>
                                </div>
                            </div>

                            {!hideCompanySelector ? (
                                <div className="mt-5 max-w-md">
                                    <label className="mb-2 block text-xs font-semibold text-slate-500">
                                        Company
                                    </label>
                                    <SelectField
                                        value={selectedCompany}
                                        onChange={handleCompanyChange}
                                        disabled={loadingManufacturers || processing || applying}
                                    >
                                        {manufacturers.map((m) => (
                                            <option key={m} value={m}>
                                                {titleizeSlug(m)}
                                            </option>
                                        ))}
                                    </SelectField>
                                </div>
                            ) : (
                                <div className="mt-5 max-w-md">
                                    <p className="text-xs font-semibold text-slate-400">Company</p>
                                    <div className="mt-1 rounded-xl border border-slate-200 bg-white/70 px-4 py-3 text-sm text-slate-800">
                                        {titleizeSlug(selectedCompany)}
                                    </div>
                                </div>
                            )}

                            <input {...getInputProps()} />

                            <div
                                {...getRootProps()}
                                className={cx(
                                    "mt-5 rounded-2xl border-2 border-dashed p-6 sm:p-8 transition-colors bg-slate-50",
                                    isDragAccept && "border-emerald-400 bg-emerald-50",
                                    isDragReject && "border-rose-400 bg-rose-50",
                                    isDragActive && !isDragReject && "border-slate-400 bg-slate-100",
                                    !isDragActive && !isDragAccept && !isDragReject && "border-slate-300 hover:border-slate-400"
                                )}
                            >
                                <div className="flex gap-12 lg:gap-32 flex-col lg:flex-row items-center justify-center">
                                    <div className="flex flex-col items-center justify-center text-center lg:items-start lg:text-left">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200">
                                            <UploadCloud className="h-7 w-7 text-slate-700" />
                                        </div>

                                        <div className="mt-4">
                                            <h3 className="text-sm sm:text-base font-bold text-slate-900">
                                                {isDragActive ? "Drop your PDF here" : "Drag and drop your PDF here"}
                                            </h3>
                                            <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                                PDF only, one file at a time
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={open}
                                            className="mt-4 h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                            disabled={processing || applying}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <FileText className="h-4 w-4 text-slate-400" />
                                                {pdfFile ? "Replace PDF" : "Browse file"}
                                            </span>
                                        </button>
                                    </div>

                                    <div className="flex flex-col items-center text-center">
                                        <img
                                            src="/nathers_report_example.png"
                                            alt="Example report"
                                            className="w-full max-w-[320px] rounded-2xl border border-slate-200 bg-white object-contain shadow-sm"
                                        />
                                        <p className="mt-2 text-xs text-slate-500">
                                            Example of the report format to upload
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-5 flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={runScan}
                                    className="h-11 rounded-xl bg-slate-900 px-6 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                                    disabled={processing || applying || !pdfFile || !selectedCompany || loadingManufacturers}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <Search className="h-4 w-4" />
                                        {processing ? "Processing..." : "Scan PDF"}
                                    </span>
                                </button>

                                <button
                                    type="button"
                                    onClick={resetAll}
                                    className="h-11 rounded-xl border border-slate-200 bg-white px-6 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                    disabled={processing || applying}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        <RotateCcw className="h-4 w-4 text-slate-400" />
                                        Reset
                                    </span>
                                </button>
                            </div>

                            {selectedCompany && (
                                <div className="mt-3 text-xs sm:text-sm text-slate-600">
                                    Current company scope:{" "}
                                    <span className="font-semibold text-slate-900">
                                        {titleizeSlug(selectedCompany)}
                                    </span>
                                </div>
                            )}

                            {pdfFile && (
                                <div className="mt-1 text-xs sm:text-sm text-slate-600">
                                    Selected: <span className="font-semibold text-slate-900">{pdfFile.name}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Summary + Material */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-5 py-5 sm:px-6 sm:py-6">
                        <div className="flex items-start gap-4 text-left">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                <Layers3 className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 w-full">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                                            Company scope and filters
                                        </h3>
                                        <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                            The table below only contains products from <span className="font-semibold">{companyTitle}</span>. Use the material filter to change the results.
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge tone="slate">Codes: {codes.length}</Badge>
                                        <Badge tone="emerald">Rows: {rows.length}</Badge>
                                        <Badge tone="sky">Blue glass = exact match</Badge>
                                    </div>
                                </div>

                                {unmatchedCodes.length > 0 && (
                                    <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                                        Unmatched PDF codes: {unmatchedCodes.join(", ")}
                                    </div>
                                )}

                                <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-3">
                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                        <div className="text-xs font-semibold text-slate-500">Company</div>
                                        <div className="mt-2">
                                            {hideCompanySelector ? (
                                                <div className="flex h-11 items-center rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm">
                                                    {companyTitle || titleizeSlug(selectedCompany) || "Company"}
                                                </div>
                                            ) : (
                                                <SelectField
                                                    value={selectedCompany}
                                                    onChange={handleCompanyChange}
                                                    disabled={processing || applying || loadingManufacturers}
                                                >
                                                    {manufacturers.map((m) => (
                                                        <option key={m} value={m}>
                                                            {titleizeSlug(m)}
                                                        </option>
                                                    ))}
                                                </SelectField>
                                            )}
                                        </div>
                                    </div>

                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                        <div className="text-xs font-semibold text-slate-500">Initial set</div>
                                        <div className="mt-1 text-sm font-bold text-slate-900">MAX</div>
                                    </div>

                                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                        <div className="text-xs font-semibold text-slate-500">SHGC tolerance</div>
                                        <div className="mt-1 text-sm font-bold text-slate-900">±0.02</div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </section>

            {/* Controls */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-5 py-5 sm:px-6 sm:py-6">
                        <div className="flex items-start gap-4 text-left">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                <Info className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 w-full">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                                            Selection controls
                                        </h3>
                                        <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                            Change product type, U selector, or SHGC selector, then apply the selections.
                                        </p>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="rounded-xl border border-slate-200 bg-slate-50">
                                            <SelectField
                                                value={materialFilter}
                                                onChange={handleMaterialChange}
                                                disabled={processing || applying}
                                            >
                                                <option value="all">All materials</option>
                                                {materials.map((m) => (
                                                    <option key={m} value={m}>
                                                        {m}
                                                    </option>
                                                ))}
                                            </SelectField>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => applyPresetToAll("max")}
                                            className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                            disabled={processing || applying}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <Search className="h-4 w-4" />
                                                {applying ? "Applying..." : "Apply max set"}
                                            </span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => applyPresetToAll("min")}
                                            className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                            disabled={processing || applying}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <Search className="h-4 w-4" />
                                                {applying ? "Applying..." : "Apply min set"}
                                            </span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => handleApply(rows, materialFilter, selectedCompany)}
                                            className="h-11 rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-60"
                                            disabled={processing || applying}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <Search className="h-4 w-4 text-slate-400" />
                                                {applying ? "Updating..." : "Update"}
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-[1100px] w-full border-collapse">
                                            <thead className="bg-slate-50">
                                                <tr className="border-b border-slate-200">
                                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                                                        PDF source
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                                                        Product type
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                                                        U selector
                                                    </th>
                                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                                                        SHGC selector
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {rows.length > 0 ? rows.map((row, idx) => (
                                                    <tr
                                                        key={row.row_key || idx}
                                                        className="border-b border-slate-200 last:border-b-0"
                                                    >
                                                        <td className="px-3 py-3 align-middle">
                                                            <div className="flex items-center">
                                                                <div className="min-w-[220px] w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                                                                    <div className="font-semibold text-slate-900">
                                                                        {row.source_window_id || "—"} - {row.source_product_type || "—"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td className="px-3 py-3 align-middle">
                                                            <div className="flex items-center">
                                                                <div className="min-w-[220px] w-full">
                                                                    <label className="sr-only">Product type</label>
                                                                    <SelectField
                                                                        value={row.product_type || ""}
                                                                        onChange={(e) =>
                                                                            updateRow(row.row_key, { product_type: e.target.value })
                                                                        }
                                                                        disabled={processing || applying}
                                                                    >
                                                                        <option value="" disabled>
                                                                            Select product type
                                                                        </option>
                                                                        {availableProductTypes.map((type) => (
                                                                            <option key={type} value={type}>
                                                                                {type}
                                                                            </option>
                                                                        ))}
                                                                    </SelectField>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td className="px-3 py-3 align-middle">
                                                            <div className="flex items-center">
                                                                <div className="min-w-[220px] w-full flex gap-1">
                                                                    <label className="sr-only">U selector</label>
                                                                    <NumberField
                                                                        value={row.u_target ?? ""}
                                                                        onChange={(e) =>
                                                                            updateRow(row.row_key, { u_target: e.target.value })
                                                                        }
                                                                        disabled={processing || applying || row.u_mode !== "nearest"}
                                                                    />
                                                                    <SelectField
                                                                        value={row.u_mode || "nearest"}
                                                                        onChange={(e) =>
                                                                            updateRow(row.row_key, { u_mode: e.target.value })
                                                                        }
                                                                        disabled={processing || applying}
                                                                    >
                                                                        <option value="max">Max</option>
                                                                        <option value="min">Min</option>
                                                                        <option value="nearest">Nearest</option>
                                                                    </SelectField>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        <td className="px-3 py-3 align-middle">
                                                            <div className="flex items-center">
                                                                <div className="min-w-[220px] w-full flex gap-1">
                                                                    <label className="sr-only">SHGC selector</label>
                                                                    <NumberField
                                                                        value={row.s_target ?? ""}
                                                                        onChange={(e) =>
                                                                            updateRow(row.row_key, { s_target: e.target.value })
                                                                        }
                                                                        disabled={processing || applying || row.s_mode !== "nearest"}
                                                                    />
                                                                    <SelectField
                                                                        value={row.s_mode || "nearest"}
                                                                        onChange={(e) => {
                                                                            const nextMode = e.target.value;
                                                                            updateRow(row.row_key, {
                                                                                s_mode: nextMode,
                                                                                s_target:
                                                                                    nextMode === "unlimited"
                                                                                        ? ""
                                                                                        : row.s_target ?? row.source_s ?? "",
                                                                            });
                                                                        }}
                                                                        disabled={processing || applying}
                                                                    >
                                                                        <option value="max">Max</option>
                                                                        <option value="min">Min</option>
                                                                        <option value="nearest">Nearest</option>
                                                                        <option value="unlimited">Unlimited</option>
                                                                    </SelectField>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )) : (
                                                    <tr className="border-b border-slate-200 last:border-b-0">
                                                        <td colSpan={4} className="px-3 py-8 text-center text-sm text-slate-500">
                                                            No resolved rows yet. You can still adjust filters and rerun the scan.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="mt-3 text-xs sm:text-sm text-slate-600">
                                    All resolved products come only from <span className="font-semibold">{companyTitle}</span>
                                    {materialFilter !== "all" ? (
                                        <>
                                            {" "}with material <span className="font-semibold">{materialFilter}</span>
                                        </>
                                    ) : (
                                        <> across all materials</>
                                    )}.
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            {/* Export / Preview table */}
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="px-5 py-5 sm:px-6 sm:py-6">
                        <div className="flex items-start gap-4 text-left">
                            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                <Table2 className="h-5 w-5" />
                            </div>

                            <div className="min-w-0 w-full">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                                            Table preview and export
                                        </h3>
                                        <p className="mt-1 text-xs sm:text-sm text-slate-500">
                                            You can export the table as an image to share with others.
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleExport}
                                        className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100 disabled:opacity-60"
                                        disabled={processing || applying || exporting}
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <Download className="h-4 w-4 text-slate-400" />
                                            {exporting ? "Exporting..." : "Export table as image"}
                                        </span>
                                    </button>
                                </div>

                                <div className="mt-4 overflow-hidden bg-[#ffffff]">
                                    <div className="grid grid-cols-[28px_minmax(0,1fr)_28px]" ref={exportRef}>
                                        <div className="bg-[#cbddd0]" />
                                        <div className="bg-white px-6 py-5" style={{ fontFamily: "Arial, Helvetica, sans-serif" }}>
                                            <div>
                                                <div className="mb-3 text-sm font-extrabold text-[#286f40]">
                                                    Custom* windows
                                                </div>

                                                <table className="w-full border-collapse text-left text-[12px] leading-[1.25] text-slate-900">
                                                    <thead>
                                                        <tr>
                                                            <th
                                                                rowSpan={2}
                                                                className="border-b border-[#B9B9B9] px-2 py-2 font-extrabold align-middle w-[110px]"
                                                            >
                                                                Window ID
                                                            </th>
                                                            <th
                                                                rowSpan={2}
                                                                className="border-b border-[#B9B9B9] px-2 py-2 font-extrabold align-middle w-[230px]"
                                                            >
                                                                Window Description
                                                            </th>
                                                            <th
                                                                rowSpan={2}
                                                                className="border-b border-[#B9B9B9] px-2 py-2 font-extrabold text-center align-middle w-[95px]"
                                                            >
                                                                Maximum
                                                                <br />
                                                                U-value*
                                                            </th>
                                                            <th
                                                                rowSpan={2}
                                                                className="border-b border-[#B9B9B9] px-2 py-2 font-extrabold text-center align-middle w-[70px]"
                                                            >
                                                                SHGC*
                                                            </th>
                                                            <th
                                                                colSpan={2}
                                                                className="border-b border-[#B9B9B9] px-2 py-2 font-extrabold text-center w-[150px]"
                                                            >
                                                                SHGC substitution
                                                                <br />
                                                                tolerance ranges
                                                            </th>
                                                            <th
                                                                rowSpan={2}
                                                                className="border-b border-[#B9B9B9] px-2 py-2 font-extrabold align-middle w-[120px]"
                                                            >
                                                                Product type
                                                            </th>
                                                            <th
                                                                rowSpan={2}
                                                                className="border-b border-[#B9B9B9] px-2 py-2 font-extrabold align-middle w-[120px]"
                                                            >
                                                                Glass
                                                            </th>
                                                        </tr>
                                                        <tr>
                                                            <th className="border-b border-[#B9B9B9] px-2 py-1 font-bold text-center w-[75px]">
                                                                lower limit
                                                            </th>
                                                            <th className="border-b border-[#B9B9B9] px-2 py-1 font-bold text-center w-[75px]">
                                                                upper limit
                                                            </th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {rows.length > 0 ? rows.map((row, idx) => (
                                                            <tr key={row.row_key || idx}>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top font-normal">
                                                                    {row.window_id || "—"}
                                                                </td>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top">
                                                                    {row.window_description || "—"}
                                                                </td>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top text-center">
                                                                    {fmt(row.u_value, 1)}
                                                                </td>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top text-center">
                                                                    {fmt(row.shgc, 2)}
                                                                </td>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top text-center">
                                                                    {fmt(row.lower_limit, 2)}
                                                                </td>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top text-center">
                                                                    {fmt(row.upper_limit, 2)}
                                                                </td>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top">
                                                                    {row.product_type || "—"}
                                                                </td>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-2 align-top">
                                                                    {!row.glass_eligible && row.glass ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openRegisterDialog(row)}
                                                                            className="font-medium text-blue-700 hover:text-blue-800"
                                                                        >
                                                                            {row.glass}
                                                                        </button>
                                                                    ) : (
                                                                        <span className="text-slate-900">
                                                                            {row.glass || "—"}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )) : (
                                                            <tr>
                                                                <td className="border-b border-[#CFCFCF] px-2 py-8 text-center text-slate-500" colSpan={8}>
                                                                    No resolved rows to preview yet.
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                <p className="mt-3 text-[11px] font-medium text-slate-600">
                                                    This table is predictive only. For more accurate results, please perform a proper simulation.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="bg-[#cbddd0]" />
                                    </div>
                                </div>

                                <div className="mt-3 text-xs sm:text-sm text-slate-600">
                                    <p className="text-blue-700">
                                        Blue glass means the closest company row within the current active limits and material scope.
                                        <span className="font-semibold"> Click on the glass name for more details</span>
                                    </p>
                                    <p className="text-slate-700">
                                        Black glass means the resolved company row exactly matched the requested product type, U, and SHGC.
                                        <br />
                                        If SHGC is set to <span className="font-semibold">Unlimited</span>, SHGC is ignored and matching is based on product type and U only.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            {registerDialogOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
                    <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h4 className="text-lg font-bold text-slate-900 text-left">
                                    System registration required
                                </h4>
                                <p className="mt-2 text-sm leading-6 text-slate-600 text-left">
                                    This glazing/system combination may require registration before it can be used.
                                    Please contact{" "}
                                    <a
                                        href="mailto:sales@ciilock.com"
                                        className="font-semibold text-blue-700 underline underline-offset-2"
                                    >
                                        sales@ciilock.com
                                    </a>{" "}
                                    to arrange registration and receive the next steps.
                                </p>

                                {selectedRegisterRow && (
                                    <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 text-left">
                                        <div>
                                            <span className="font-semibold text-slate-900">Window ID:</span>{" "}
                                            {selectedRegisterRow.window_id || "—"}
                                        </div>
                                        <div>
                                            <span className="font-semibold text-slate-900">Window description:</span>{" "}
                                            {selectedRegisterRow.window_description || "—"}
                                        </div>
                                        <div>
                                            <span className="font-semibold text-slate-900">Product type:</span>{" "}
                                            {selectedRegisterRow.product_type || "—"}
                                        </div>
                                        <div>
                                            <span className="font-semibold text-slate-900">Glass:</span>{" "}
                                            {selectedRegisterRow.glass || "—"}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={closeRegisterDialog}
                                className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
                            >
                                Close
                            </button>
                            <a
                                href="mailto:sales@ciilock.com"
                                className="inline-flex h-11 items-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
                            >
                                Email sales
                            </a>
                        </div>
                    </div>
                </div>
            )}
            {showToastContainer && (
                <ToastContainer position="top-right" autoClose={4000} newestOnTop />
            )}
        </div>
    );
}
